import {Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {BaseChartComponent, type BatteryType, type EnergyType, type Interval} from './base-chart.component';
import {BaseChartDirective} from 'ng2-charts';
import {AverageEnergyResponse} from '../../../../api/models/average-energy-response';
import {Chart, ChartOptions} from 'chart.js';
import {de} from 'date-fns/locale';
import annotationPlugin from 'chartjs-plugin-annotation';
import {CommonModule} from '@angular/common';
import {DateTimeValue} from '../../../../api/models/date-time-value';
import {getAverageEnergyStatus} from './average-energy-response.helper';

@Component({
  selector: 'app-battery-power-chart',
  templateUrl: './chart.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BaseChartDirective, CommonModule],
})
export class BatteryPowerChartComponent extends BaseChartComponent implements OnChanges {
  type: EnergyType = 'battery'; // Define input with default value
  batteryType: BatteryType = 'power'; // Define input with default value
  @Input() override interval: Interval = 'month'; // Define input with default value
  @Input() date: Date = new Date();
  @Input() data: AverageEnergyResponse | undefined;

  // Override unit to kwh for battery power charts
  @Input() override get unit(): string {
    return  'kWh';
  }

  override set unit(value: string) {
    // Allow external setting but ignore it since we determine unit based on batteryType
  }

  monthBarChartOptions: ChartOptions = {
    plugins: {
      ...this.baseTooltipOptions,
      legend: {
        position: 'bottom', // Position the legend at the bottom
        display: true, // Enable legend display
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        stacked: true,
        border: {
          display: false,
        },
        offset: true,
      },
      x: {
        stacked: true,
        type: 'time', // Ensure time-based X-axis
        time: {
          unit: 'day', // Display days on the axis
          tooltipFormat: 'dd MM', // Tooltip format (e.g., "01 Mar")
          displayFormats: !this.isMobile()
            ? {day: 'dd MM'} // Format for the axis labels
            : {day: 'd'}, // Display only day numbers (e.g., "1", "2", "3")
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        ticks: {
          maxRotation: 90, // labels vertical for both mobile and desktop
          minRotation: 90, // labels vertical for both mobile and desktop
          callback: (value) => {
            const date = new Date(value);
            return this.isMobile()
              ? date.getDate() // Return just the day number for mobile
              : date.toLocaleDateString('de-AT', {
                  day: '2-digit',
                  month: 'numeric',
                  timeZone: this.timezone,
                });
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  yearBarChartOptions: ChartOptions = {
    plugins: {
      ...this.baseTooltipOptions,
      legend: {
        position: 'bottom', // Position the legend at the bottom
        display: true, // Enable legend display
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        stacked: true,
        beginAtZero: true,
        border: {
          display: false,
        },
        offset: true,
      },
      x: {
        stacked: true,
        type: 'time', // Use timescale for date-based X-axis
        time: {
          unit: 'month', // Group data by month
          tooltipFormat: 'MMM yyyy', // Show "Mar 2025" format in tooltips
          displayFormats: {
            month: 'MMM', // Display short month names (e.g., "Jan", "Feb")
            day: 'D', // Show day number (e.g., "1", "15", "30")
          },
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          padding: this.isMobile() ? 4 : 8,
          font: {
            size: this.isMobile() ? 10 : 12,
          },
          callback: (value) => {
            const date = new Date(value as string);
            return date.toLocaleDateString('de-AT', {month: 'short', timeZone: this.timezone});
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  constructor() {
    super();
    // Register the annotation plugin
    Chart.register(annotationPlugin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Performance battery chart received change', changes);
    // Reset the chart data when type or batteryType changes
    if (changes['data'] || changes['batteryType']) {
      this.onResourceChange();
    }
  }

  private onResourceChange() {
    if (!this.data) {
      return; // Exit if no data is available
    }
    this.initializeChartData();
  }

  private initializeChartData() {
    console.log('initialize Performance Chart');
    if (!this.data) {
      return; // Exit if no data is available
    }

    const datasets: any[] = [];
    const allLabels: Date[][] = [];

    // Process both positive and negative battery power
    const positiveData = getAverageEnergyStatus(this.data, 'batteryPowerPositive');
    const negativeData = getAverageEnergyStatus(this.data, 'batteryPowerNegative');

    if (!positiveData || !negativeData || !positiveData.deviceTimeValues || !negativeData.deviceTimeValues) {
      return;
    }

    const deviceColors = new Map<string, string>();
    let nextColorIndex = 1;
    const getDeviceColor = (deviceKey: string): string => {
      const existingColor = deviceColors.get(deviceKey);
      if (existingColor) {
        return existingColor;
      }

      const color = this.getGreen(nextColorIndex++);
      deviceColors.set(deviceKey, color);
      return color;
    };

    // Process positive power data
    Object.entries(positiveData.deviceTimeValues).forEach(([key, timeValues]) => {
      // Type guard to ensure timeValues is an array of DateTimeValue
      if (!Array.isArray(timeValues) || !this.isThereData(timeValues as DateTimeValue[])) return;
      // Ensure timeValues is complete
      const completedTimeValues: DateTimeValue[] = this.completeData(timeValues as DateTimeValue[], this.interval, this.date, 'zero', positiveData.timeBucket);
      // Ensure all values are positive
      const rawTimeValues: DateTimeValue[] = completedTimeValues.map((tv) => ({
        ...tv,
        value: tv.value !== undefined && tv.value !== null ? Math.abs(tv.value) : tv.value,
      }));

      // Extract labels from this dataset
      const datasetLabels: Date[] = [];
      completedTimeValues.forEach((item) => {
        const date = new Date(item.read ?? '');
        if (!isNaN(date.getTime())) {
          datasetLabels.push(date);
        }
      });
      allLabels.push(datasetLabels);

      const positiveColor = getDeviceColor(key);
      const legendLabel = positiveData.deviceNames?.[key] || key;
      datasets.push({
        label: `${legendLabel} (Laden)`,
        data: rawTimeValues.map((v) => v.value),
        borderColor: positiveColor, //'#4CAF50', // Green for positive
        backgroundColor: positiveColor,// '#4CAF50',
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0.1,
        cubicInterpolationMode: 'monotone',
        spanGaps: false,
        borderWidth: 1,
        stack: 'stack1',
        base: 0,
      });

    });

    // Process negative power data
    Object.entries(negativeData.deviceTimeValues).forEach(([key, timeValues]) => {
      // Type guard to ensure timeValues is an array of DateTimeValue
      if (!Array.isArray(timeValues) || !this.isThereData(timeValues as DateTimeValue[])) return;
      // Ensure timeValues is complete
      const completedTimeValues: DateTimeValue[] = this.completeData(timeValues as DateTimeValue[], this.interval, this.date, 'zero', negativeData.timeBucket);
      // Ensure all values are negative
      const rawTimeValues: DateTimeValue[] = completedTimeValues.map((tv) => ({
        ...tv,
        value: tv.value !== undefined && tv.value != null ? -Math.abs(tv.value) : tv.value,
      }));

      // Extract labels from this dataset
      const datasetLabels: Date[] = [];
      completedTimeValues.forEach((item) => {
        const date = new Date(item.read ?? '');
        if (!isNaN(date.getTime())) {
          datasetLabels.push(date);
        }
      });
      allLabels.push(datasetLabels);
      const legendLabel = negativeData.deviceNames?.[key] || key;
      const negativeColor = getDeviceColor(key);
      datasets.push({
        label: `${legendLabel} (Entladen)`,
        data: rawTimeValues.map((v) => v.value),
        borderColor: negativeColor, //'#3d6c26', // Red for negative #F44336 //dunkles grün: #3d6c26 //dunkles rot 9c1f18// blau: 00B6EE
        backgroundColor: negativeColor, //'#3d6c26',
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0.1,
        cubicInterpolationMode: 'monotone',
        spanGaps: false,
        borderWidth: 1,
        stack: 'stack1',
        base: 0,
      });

    });

    // Process all labels to ensure they match
    const labels = this.processLabels(allLabels);
    this.updateChart(labels, datasets);
  }

  private updateChart(labels: any[], datasets: any[]) {
    super.updateChartType(this.interval);
    this.chartOptions.set(this.getCustomChartOptions());
    this.chartData.set({
      labels: labels,
      datasets: datasets,
    });
    if (this.chart) {
      this.chart.update();
    }
  }

  private getCustomChartOptions() {
    switch (this.interval) {
      case 'month':
        return this.monthBarChartOptions;
      case 'year':
        return this.yearBarChartOptions;
      default:
        return this.monthBarChartOptions;
    }
  }


  protected getGreen(index: number): string {
    const root = document.documentElement;
    const bluePalette = [
      getComputedStyle(root).getPropertyValue('--color-battery-green').trim(), // primary blue from CSS variable
      '#71B451', // standard green
      '#8BC34A', // light green
      '#558B2F', // deep green
      '#9CCC65', // fresh/sky green
      '#2E7D32', // dark green
    ];
    return bluePalette[index % bluePalette.length];
  }

  protected getDarkGreen(index: number): string {
    const root = document.documentElement;
    const bluePalette = [
      getComputedStyle(root).getPropertyValue('--color-battery-red').trim(), // primary blue from CSS variable
      '#9BD67D', // light green – a softer, pastel tone
      '#71B451', // standard green – your base color
      '#5E9B41', // deep green – slightly desaturated and darker
      '#4C7F34', // forest green – darker with more depth
      '#3A662A', // dark green – rich and shadowy
    ];
    return bluePalette[index % bluePalette.length];
  }
}
